import _ from 'underscore';
import { mergeClasses } from '@fluentui/react-components';
import React, { useCallback, useMemo, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import {
  AdsStudioBreadcrumb,
  SearchResultFilterBar,
  FilterContext,
  FolderAssetType,
  AdsStudioSpinner,
  ManualAddedImageSource,
  useAdsStudioServices,
  HiddenDivForAnnounce,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { Overlay } from '@bingads-webui-react/fabric-control';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { usePageActiveTimeTracker } from '@bingads-webui-campaign-react/common-hooks';

import { ImageGridView } from '../images-page-component/components';
import { useSearchResultViewStyle } from '../styles';

const ROOT_KEY = 'home-root';
const SEARCH_KEY = 'default-search-query';

export const SearchResultView = ({
  i18n,
  search,
  navigation,
  logger,
  dataService,
  notificationApi,
  reload,
  pagePerfMarker: searchPagePerfMarker,
  setDataLoaded,
}) => {
  const classes = useSearchResultViewStyle();

  const {
    assets,
    loadData,
  } = dataService;

  const cropDataService = useAdsStudioServices();

  const [isPageInited, setPageInited] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [announcedText, setAnnouncedText] = useState('');

  const queries = queryString.parse(search);
  const { query: defaultSearchQuery = '' } = queries || {};
  const [cachedSearchText, setCachedSearchText] = useState(defaultSearchQuery);

  const { filterBar } = useContext(FilterContext);

  const {
    searchText = '',
  } = useMemo(() => filterBar.getFilters(), [filterBar]);

  const breadcrumbItems = useMemo(() => {
    const result = [{
      key: ROOT_KEY,
      text: i18n.getString(_TL_('Home')),
    }, {
      key: SEARCH_KEY,
      text: cachedSearchText,
    }];

    return result;
  }, [cachedSearchText, i18n]);

  const onBreadcrumbItemClick = useCallback((item) => {
    if (item && item.key === ROOT_KEY) {
      navigation.gotoHomePage();
    }
  }, [navigation]);

  const onFailedToLoadData = useCallback(() => {
    notificationApi.showWarning(i18n.getString(_TL_('Failed to load images. Please refresh the page and try again.')));
  }, [i18n, notificationApi]);

  const newAssets = useMemo(() => (_.map(assets, (item) => {
    const { folderAssetType } = item || {};
    if (folderAssetType) {
      return {
        ...item,
        manualAddedImageSourceType: ManualAddedImageSource.Library,
      };
    }
    return {
      folderAssetType: FolderAssetType.Image,
      manualAddedImageSourceType: ManualAddedImageSource.ShutterStock,
      id: item.Id,
      url: item.Preview && item.Preview.Url,
    };
  })), [assets]);

  // init page
  useEffect(() => {
    if (!isPageInited) {
      setPageInited(true);
    }
  }, [isPageInited]);

  usePageActiveTimeTracker(logger, 'Search result');

  useEffect(() => {
    setClarityTag({ LoadAdsStudioSearchResultPage: true });
  }, []);

  // load data for initial page load
  useEffect(() => {
    if (!isPageInited) {
      // wait until page is initialized
      return;
    }

    if (reload) { // Important! Add a trivial log so that reload will be an explicit dep.
      logger.logTrace('force to reload');
    }

    if (_.isEmpty(searchText.trim())) {
      // Don't reload when search text is empty
      return;
    }

    setCachedSearchText(searchText);

    searchPagePerfMarker.willFetchData();
    setLoadingData(true);
    loadData({
      assetTypeFilter: FolderAssetType.Image,
      isGlobalSearch: true,
      searchText,
      perfMarker: searchPagePerfMarker.createChild('searchResultPageLoadData'),
    })
      .catch(() => {
        onFailedToLoadData();
      }).finally(() => {
        setLoadingData(false);
        searchPagePerfMarker.willRender();
        setDataLoaded();
      });
  }, [isPageInited, loadData, onFailedToLoadData, searchText, logger, reload]);

  const isPageLoading = !isPageInited || loadingData;

  useEffect(() => {
    if (!isPageLoading && assets && assets.length > 0) {
      const count = assets && assets.length;
      setAnnouncedText(i18n.getString(_TL_('{{Count}} results found.'), { Count: count }));
    } else {
      setAnnouncedText('');
    }
  }, [assets, i18n, isPageLoading]);

  return (
    <div className={mergeClasses('search-result-page', classes.searchResultView)}>
      {isPageLoading && (
        <Overlay
          styles={{ root: { marginLeft: 266 } }}
        >
          <AdsStudioSpinner className="page-load-spinner" useCustomSize customStyle={{ margin: 100 }} />
        </Overlay>
      )}
      <div className="search-page-header">
        <AdsStudioBreadcrumb
          items={breadcrumbItems}
          onItemClick={onBreadcrumbItemClick}
          maxLengthOfItem={100}
          customItemStyle={{
            breadcrumbButton: classes.breadcrumbButton,
            breadcrumbButtonCurrent: mergeClasses('curent-breadcrumb-button', classes.breadcrumbButtonCurrent),
          }}
        />
        <div className={classes.headerContainer}>
          <div className={classes.headerTitle}>
            {i18n.getString(_TL_('Images'))}
          </div>
          <div className={classes.headerDescriptions}>
            {i18n.getString(_TL_('Images that match your search for “'))}
            <span className={mergeClasses('search-text', classes.defaultSearchQuery)}>{cachedSearchText}</span>
            {i18n.getString(_TL_('”'))}
          </div>
        </div>
        <SearchResultFilterBar
          i18n={i18n}
          logger={logger}
          searchboxPlaceHolder={i18n.getString(_TL_('Search for assets'))}
          defaultSearchText={defaultSearchQuery}
        />
      </div>
      <ImageGridView
        isSearchResultPage
        i18n={i18n}
        logger={logger}
        dataService={{
          ...dataService,
          assets: newAssets,
        }}
        onFailedToLoadData={onFailedToLoadData}
        searchText={searchText}
        navigation={navigation}
        cropDataService={cropDataService}
      />
      <HiddenDivForAnnounce toAnnounce={announcedText} />
    </div>
  );
};

SearchResultView.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  search: PropTypes.string.isRequired,
  navigation: PropTypes.shape({}).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  dataService: PropTypes.shape({}),
  notificationApi: PropTypes.shape({}),
  reload: PropTypes.shape({}),
};

SearchResultView.defaultProps = {
  dataService: {},
  notificationApi: {},
  reload: null,
};
